"use client"

import { useState, useEffect } from "react"
import { useAuth0 } from '@auth0/auth0-react';
import { getApiBaseUrl } from "../config/apiBase"

const useObjective = (actionId, action) => {
  console.log("useObjective hook initialized with actionId:", actionId)

  const [objective, setObjective] = useState("")
  const [suggestions, setSuggestions] = useState([])
  const [comments, setComments] = useState([])
  const [newSuggestion, setNewSuggestion] = useState("")
  const [newComment, setNewComment] = useState("")
  const [activeTab, setActiveTab] = useState("current")
  const [loading, setLoading] = useState(true)
  const { getAccessTokenSilently } = useAuth0();

  const apiUrl = getApiBaseUrl()
  console.log("API URL:", apiUrl)

  const fetchObjectiveData = async () => {
    console.log("fetchObjectiveData called with actionId:", actionId)
    console.log("apiUrl:", apiUrl)

    if (!actionId) {
      console.log("No actionId provided, skipping fetch")
      setLoading(false)
      return
    }

    try {
      setLoading(true)
       const token = await getAccessTokenSilently({
                authorizationParams: {
                    audience: process.env.REACT_APP_API_AUDIENCE,
                }
            });
      console.log("Token exists:", !!token)

      const objectiveUrl = `${apiUrl}/api/actions/${actionId}/refinement/modules/objective`
      console.log("Fetching objective from:", objectiveUrl)

      // Fetch the current objective
      const objectiveResponse = await fetch(objectiveUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      console.log("Objective response status:", objectiveResponse.status)

      if (objectiveResponse.ok) {
        const objectiveData = await objectiveResponse.json()
        console.log("Objective data received:", objectiveData)
        setObjective(objectiveData.content || action?.actionStatement || "")
      } else {
        console.error("Objective response not ok:", objectiveResponse.status, objectiveResponse.statusText)
      }

      // Fetch suggestions
      const suggestionsUrl = `${apiUrl}/api/actions/${actionId}/refinement/modules/objective/suggestions`
      console.log("Fetching suggestions from:", suggestionsUrl)

      const suggestionsResponse = await fetch(suggestionsUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      console.log("Suggestions response status:", suggestionsResponse.status)

      if (suggestionsResponse.ok) {
        const suggestionsData = await suggestionsResponse.json()
        console.log("Suggestions data received:", suggestionsData)
        setSuggestions(suggestionsData || [])
      } else {
        console.error("Suggestions response not ok:", suggestionsResponse.status, suggestionsResponse.statusText)
      }

      // Fetch comments
      const commentsUrl = `${apiUrl}/api/actions/${actionId}/refinement/modules/objective/comments`
      console.log("Fetching comments from:", commentsUrl)

      const commentsResponse = await fetch(commentsUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      console.log("Comments response status:", commentsResponse.status)

      if (commentsResponse.ok) {
        const commentsData = await commentsResponse.json()
        console.log("Comments data received:", commentsData)
        setComments(commentsData || [])
      } else {
        console.error("Comments response not ok:", commentsResponse.status, commentsResponse.statusText)
      }
    } catch (error) {
      console.error("Error fetching objective data:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmitSuggestion = async () => {
    console.log("handleSubmitSuggestion called with:", newSuggestion)

    if (!newSuggestion.trim()) {
      console.log("Empty suggestion, skipping submit")
      return
    }

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })
      const url = `${apiUrl}/api/actions/${actionId}/refinement/modules/objective/suggestions`

      console.log("Submitting suggestion to:", url)
      console.log("Suggestion content:", newSuggestion)

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ content: newSuggestion }),
      })

      console.log("Submit suggestion response status:", response.status)

      if (response.ok) {
        setNewSuggestion("")
        await fetchObjectiveData()
      } else {
        console.error("Failed to submit suggestion:", response.status, response.statusText)
      }
    } catch (error) {
      console.error("Error submitting suggestion:", error)
    }
  }

  const handleSubmitComment = async () => {
    console.log("handleSubmitComment called with:", newComment)

    if (!newComment.trim()) {
      console.log("Empty comment, skipping submit")
      return
    }

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })
      const url = `${apiUrl}/api/actions/${actionId}/refinement/modules/objective/comments`

      console.log("Submitting comment to:", url)

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ content: newComment }),
      })

      console.log("Submit comment response status:", response.status)

      if (response.ok) {
        setNewComment("")
        await fetchObjectiveData()
      } else {
        console.error("Failed to submit comment:", response.status, response.statusText)
      }
    } catch (error) {
      console.error("Error submitting comment:", error)
    }
  }

  const handleVote = async (suggestionId, voteType) => {
    console.log("handleVote called with:", suggestionId, voteType)

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })
      const url = `${apiUrl}/api/actions/${actionId}/refinement/suggestions/${suggestionId}/vote`

      console.log("Submitting vote to:", url)

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ voteType }),
      })

      console.log("Vote response status:", response.status)

      if (response.ok) {
        await fetchObjectiveData()
      } else {
        console.error("Failed to submit vote:", response.status, response.statusText)
      }
    } catch (error) {
      console.error("Error submitting vote:", error)
    }
  }

  useEffect(() => {
    console.log("useEffect triggered with actionId:", actionId)
    console.log("useEffect will call fetchObjectiveData")
    fetchObjectiveData()
  }, [actionId])

  console.log("useObjective hook returning state:", {
    objective: objective.length,
    suggestionsCount: suggestions.length,
    commentsCount: comments.length,
    loading,
  })

  return {
    // State
    objective,
    suggestions,
    comments,
    newSuggestion,
    newComment,
    activeTab,
    loading,

    // Actions
    setActiveTab,
    setNewSuggestion,
    setNewComment,
    handleSubmitSuggestion,
    handleSubmitComment,
    handleVote,
    fetchObjectiveData,
  }
}

export default useObjective
